import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from './Navbar';
import { endpoints } from '../config/apiConfig';

const Formulario = () => {
  const navigate = useNavigate();
  const [cedula, setCedula] = useState('');
  const [preguntas, setPreguntas] = useState([]);
  const [formData, setFormData] = useState({});
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [periodoActivo, setPeriodoActivo] = useState(null);
  const [nombreEstudiante, setNombreEstudiante] = useState('');
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const documento = localStorage.getItem('documento');
        if (!documento) {
          navigate('/login');
          return;
        }
  
        // Verificar período activo
        const periodoResponse = await fetch(endpoints.periodoActivo);
        const periodoData = await periodoResponse.json();
  
        if (periodoData.existe) {
          setPeriodoActivo(periodoData.periodo);
          localStorage.setItem('periodoActivo', JSON.stringify(periodoData.periodo));
        }
  
        // Validación del usuario y obtención del nombre
        const validacionResponse = await fetch(endpoints.verificarEstudiante(documento));
        const validacionData = await validacionResponse.json();
  
        if (!validacionData.existe) {
          navigate('/login');
          return;
        }
  
        setNombreEstudiante(validacionData.estudiante || '');
  
        // Obtener preguntas y datos del estudiante
        const preguntasResponse = await fetch(endpoints.preguntasEstudiante(documento));
        const preguntasData = await preguntasResponse.json();
        
        if (preguntasData.error) {
          toast.error(
            <div>
              <p>{preguntasData.error}</p>
              <p className="mt-2 text-sm">
                {preguntasData.mensaje_detallado}
              </p>
              <p className="mt-2 text-sm font-semibold">
                Campos faltantes: {preguntasData.campos_faltantes.join(', ')}
              </p>
            </div>,
            {
              position: "bottom-right",
              autoClose: 10000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true
            }
          );
          navigate('/login');
          return;
        }
        
        setCedula(preguntasData.cedula);
        setNombreEstudiante(preguntasData.nombre);
        const preguntasArray = Array.isArray(preguntasData.preguntas) ? preguntasData.preguntas : [];
        setPreguntas(preguntasArray);
        
        const initialFormData = {};
        preguntasArray.forEach(pregunta => {
          initialFormData[`pregunta${pregunta.id}`] = '';
        });
        setFormData(initialFormData);
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error al cargar los datos. Por favor, intente nuevamente.', {
          position: "bottom-right",
          autoClose: 5000
        });
      }
    };
    fetchData();
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!periodoActivo) {
      setMensaje({ 
        tipo: 'error', 
        texto: 'El período actual no se encuentra activado. No es posible enviar las respuestas.' 
      });
      return;
    }

    try {
      const documento = localStorage.getItem('documento');
      
      const datosAEnviar = {
        documento: documento,
        periodo_id: periodoActivo.id,
        respuestas: Object.keys(formData).map(key => ({
          pregunta_id: parseInt(key.replace('pregunta', '')),
          respuesta_seleccionada: formData[key]
        })).filter(respuesta => respuesta.respuesta_seleccionada !== '')
      };

      const response = await fetch(endpoints.validarRespuestas, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(datosAEnviar)
      });

      if (!response.ok) {
        throw new Error('Error al validar las respuestas');
      }

      const resultado = await response.json();
      
      if (resultado.todas_correctas) {
        setMensaje({ 
          tipo: 'exito', 
          texto: '¡Verificación exitosa! Redirigiendo...'
        });
        // Esperar un momento antes de redirigir para que el usuario vea el mensaje
        setTimeout(() => {
          navigate('/verMatricula');
        }, 1500);
      } else {
        

        setMensaje({ 
          tipo: 'error', 
          texto: `Las respuestas proporcionadas no son correctas. Por favor, intente nuevamente.`
        });
      }
    } catch (error) {
      console.error('Error en la validación:', error);
      setMensaje({ 
        tipo: 'error', 
        texto: 'Error al validar las respuestas. Por favor, intente nuevamente.'
      });
    }
  };
  return (
    <>
      <Navbar />
      <div className="bg-gray-100 min-h-screen p-8">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-6">
            Revisión de matrícula de {nombreEstudiante}
          </h1>
    
          {mensaje.texto && (
            <div className={`p-4 mb-4 rounded ${
              mensaje.tipo === 'exito' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {mensaje.texto}
            </div>
          )}
    
          <form onSubmit={handleSubmit}>
            {preguntas.map((pregunta) => (
              <div key={pregunta.id} className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {pregunta.pregunta}
                </label>
                <select
                  name={`pregunta${pregunta.id}`}
                  value={formData[`pregunta${pregunta.id}`]}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Seleccione una respuesta</option>
                  {pregunta.opciones?.map((opcion, index) => (
                    <option key={index} value={opcion}>
                      {opcion}
                    </option>
                  ))}
                </select>
              </div>
            ))}
    <button
              type="submit"
              disabled={!periodoActivo}
              className={`w-full px-4 py-2 rounded-md font-medium ${
                periodoActivo 
                  ? 'bg-neutral-900 text-white hover:bg-neutral-800' 
                  : 'bg-gray-400 text-gray-200 cursor-not-allowed'
              }`}
            >
              {periodoActivo ? 'Enviar Respuestas' : 'Período Inactivo'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default Formulario;
